import React, { useState, useRef } from 'react';
import Toast from 'bootstrap/js/dist/toast';
import LoanQuoteStep3 from './LoanQuoteStep3';
import LoanQuoteStep2 from './LoanQuoteStep2';
import LoanQuoteStep1 from './LoanQuoteStep1';

const LoanQuoteForm = ({ formType }) => {

    const EMPTY_LOAN_QUOTATION = {};
    const EMPTY_LOAN_QUOTATION_INPUT = {
        loanAmountString: "",
        loanTermCode: "0",
        companySearchTerm: "",
        selectedCompany: "",
        soleTraderInput: "",
        creditRatingCode: "1",
        contactNameInput: "",
        emailInput: "",
        phoneInput: "",
        loanQuotation: {}
    }
    
    const [step, setStep] = useState(1);
    const [loanQuotation, setLoanQuotation] = useState(EMPTY_LOAN_QUOTATION);
    const [loanQuotationInput, setLoanQuotationInput] = useState(EMPTY_LOAN_QUOTATION_INPUT);
    const [submittingQuotationRequest, setSubmittingQuotationRequest] = useState(false);
    const [submittingApplication, setSubmittingApplication] = useState(false);
    const [errors, setErrors] = useState({});
    const [toastMessage, setToastMessage] = useState('');

    const toastRef = useRef(null);

    const showToast = (message) => {
        setToastMessage(message);
        const toastElement = toastRef.current;
        if (toastElement) {
            const toast = new Toast(toastElement, { delay: 5000 });
            toast.show();
        }
    }

    const convertLoanTerm = (loanTermCode) => {
        if (loanTermCode === "0") return 24;
        if (loanTermCode === "1") return 36;
        if (loanTermCode === "2") return 48;
        if (loanTermCode === "3") return 60;
        if (loanTermCode === "4") return 72;
        return 84;
    }

    const convertCreditRating = (creditRatingCode) => {
        if (creditRatingCode === "0") return "excellent";
        if (creditRatingCode === "1") return "average";
        return "poor";
    }

    const handleSubmitQuote = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        setSubmittingQuotationRequest(true);
        try {
            let loanQuotationRequest = getLoanQuotationRequest(loanQuotationInput);
            const response = await fetch("/wp-json/loan-quote/v1/calculate", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ...loanQuotationRequest, formType })
            });

            if (!response.ok) throw new Error('Quotation submission failed');
            const quotationData = await response.json();
            setLoanQuotation(quotationData);
            setStep(2);
        } catch (ex) {
            showToast('There was a problem sending the information. Please try again.');
        } finally {
            setSubmittingQuotationRequest(false);
        }
    }

    const getLoanQuotationRequest = (loanQuotationInput) => {
        let loanQuotationRequest = {
            loanAmount: parseFloat(loanQuotationInput.loanAmountString),
            loanTerm: convertLoanTerm(loanQuotationInput.loanTermCode),
            creditRating: convertCreditRating(loanQuotationInput.creditRatingCode),
            selectedCompany: loanQuotationInput.selectedCompany,
            soleTrader: loanQuotationInput.soleTraderInput.trim(),
            contactName: loanQuotationInput.contactNameInput.trim(),
            email: loanQuotationInput.emailInput.trim(),
            phone: loanQuotationInput.phoneInput.trim()
        };

        return loanQuotationRequest;
    }

    const getLoanApplicationRequest = (loanQuotationInput, loanQuotation) => {
        let loanApplicationRequest = {
            selectedCompany: loanQuotationInput.selectedCompany,
            soleTrader: loanQuotationInput.soleTraderInput.trim(),
            contactName: loanQuotationInput.contactNameInput.trim(),
            email: loanQuotationInput.emailInput.trim(),
            phone: loanQuotationInput.phoneInput.trim(),
            loanQuotation
        };

        return loanApplicationRequest;
    }

    const handleSubmitApplication = async (e) => {
        e.preventDefault();
        setSubmittingApplication(true);
        try {
            let loanApplicationRequest = getLoanApplicationRequest(loanQuotationInput, loanQuotation);

            const response = await fetch("/wp-json/loan-quote/v1/apply", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ...loanApplicationRequest, formType })
            });

            if (!response.ok) throw new Error('Application submission failed');
            setStep(3);
        } catch (ex) {
            showToast('There was a problem sending the information. Please try again.');
        } finally {
            setSubmittingApplication(false);
        }
    }

    const handleResetForm = async (e) => {
        e.preventDefault();
        setLoanQuotationInput(EMPTY_LOAN_QUOTATION_INPUT);
        setLoanQuotation(EMPTY_LOAN_QUOTATION);
        setSubmittingQuotationRequest(false);
        setSubmittingApplication(false);
        setStep(1);
    }

    const validateForm = () => {
        let newErrors = {};

        if (!loanQuotationInput.loanAmountString || loanQuotationInput.loanAmountString < 3000) {
            newErrors.loanAmount = "The minimum loan is £3,000";
        } else if (loanQuotationInput.loanAmountString - Math.trunc(loanQuotationInput.loanAmountString) > 0) {
            newErrors.loanAmount = "Whole pounds only";
        }
        if (!loanQuotationInput.selectedCompany && (!loanQuotationInput.soleTraderInput || !/\S+((\W\S)+|(\S[\S\W]?){1,50})/.test(loanQuotationInput.soleTraderInput))) {
            newErrors.company = "Select your limited company or provide your sole-trader/partner name";
        }
        if (!loanQuotationInput.contactNameInput || !/\S+((\W\S)+|(\S[\S\W]?){1,50})/.test(loanQuotationInput.contactNameInput)) newErrors.contactNameInput = "Enter a contact name";
        if (!loanQuotationInput.emailInput || !/^\w+[\w-]*@(\w+[\w-]*\.)+(\w){2,}$/.test(loanQuotationInput.emailInput)) newErrors.emailInput = "Enter a valid email";
        if (!loanQuotationInput.phoneInput || !/^\s*\+?[0-9]+(\s?\(?[0-9]+\)?\s?-?){5,}((ext\.?|x)\s?[0-9]+)?$/.test(loanQuotationInput.phoneInput)) newErrors.phoneInput = "Enter a valid phone number";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    return (
            <div className="lqc container mt-1 my-1">
                <div class="mx-auto">
                    {step === 1 && (
                        <LoanQuoteStep1 
                            onSubmitQuote={handleSubmitQuote}
                            loanQuotationInput={loanQuotationInput}
                            onChangeLoanQuotationInput={setLoanQuotationInput}
                            isSubmitting={submittingQuotationRequest}
                            errors={errors}
                        />
                    )}

                    {step === 2 && (
                        <LoanQuoteStep2 
                            loanQuotation={loanQuotation}
                            onSubmitApplication={handleSubmitApplication} 
                            onDeclineApplication={handleResetForm}
                            isSubmitting={submittingApplication}
                        />
                    )}
                    
                    {step === 3 && (
                        <LoanQuoteStep3 onNewQuoteClick={handleResetForm} />
                    )}

                    <div className="position-relative mt-4 d-flex justify-content-center">
                        <div
                            id="error-toast"
                            className="toast align-items-center text-bg-danger border-0"
                            role="alert"
                            aria-live="assertive"
                            aria-atomic="true"
                            ref={toastRef}
                        >
                            <div className="d-flex">
                                <div className="toast-body">
                                    {toastMessage}
                                </div>
                                <button 
                                    type="button"
                                    className="btn-close btn-close-white me-2 m-auto capitalize text-sm" 
                                    data-bs-dismiss="toast" 
                                    aria-label="Close">
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
    );
};

export default LoanQuoteForm;
